import React, { useState, useEffect } from 'react';
import { X, FileText, Search, CheckCircle, AlertTriangle, Trash2 } from 'lucide-react';
import api from '../../services/api';
import SearchableSelect from '../ui/SearchableSelect';

interface Vendor {
  id: number;
  name: string;
}

interface EligibleItem {
  id: number;
  itinerary_id: number;
  vendor_id: number;
  vendor_name: string;
  name: string;
  item_type: string;
  estimated_amount: number;
  status: string;
  due_date: string | null;
  guest_name: string | null;
  quotation_number: string | null;
  agency_name: string | null;
  already_invoiced: boolean;
}

interface SelectedItem extends EligibleItem {
  corrected_amount: number;
  // '' means "use the common due date"; a value overrides it for this item only
  due_date_override: string;
}

interface BulkInvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const BulkInvoiceModal: React.FC<BulkInvoiceModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [step, setStep] = useState<1 | 2>(1);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [selectedVendorId, setSelectedVendorId] = useState<number | null>(null);
  const [searchText, setSearchText] = useState('');
  const [searchResults, setSearchResults] = useState<EligibleItem[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedItems, setSelectedItems] = useState<Map<number, SelectedItem>>(new Map());
  const [invoiceFile, setInvoiceFile] = useState<File | null>(null);
  const [commonDueDate, setCommonDueDate] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  useEffect(() => {
    if (!isOpen) return;
    setStep(1);
    setSelectedVendorId(null);
    setSearchText('');
    setSearchResults([]);
    setSelectedItems(new Map());
    setInvoiceFile(null);
    setCommonDueDate('');
    setNotes('');
    setError('');

    const loadVendors = async () => {
      try {
        const response = await api.get('/vendors/');
        if (response.data.success) {
          setVendors(response.data.data.map((v: any) => ({ id: v.id, name: v.name })));
        }
      } catch (err) {
        console.error('Error loading vendors:', err);
      }
    };
    loadVendors();
  }, [isOpen]);

  useEffect(() => {
    if (!selectedVendorId) {
      setSearchResults([]);
      return;
    }
    const handle = setTimeout(async () => {
      setSearching(true);
      try {
        const response = await api.get('/booking/invoice-eligible-items', {
          params: { vendor_id: selectedVendorId, search: searchText || undefined }
        });
        if (response.data.success) {
          setSearchResults(response.data.data);
        }
      } catch (err) {
        console.error('Error searching items:', err);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [selectedVendorId, searchText]);

  if (!isOpen) return null;

  const addItem = (item: EligibleItem) => {
    if (item.already_invoiced) return;
    setSelectedItems(prev => {
      const next = new Map(prev);
      next.set(item.id, { ...item, corrected_amount: item.estimated_amount, due_date_override: '' });
      return next;
    });
  };

  const removeItem = (itemId: number) => {
    setSelectedItems(prev => {
      const next = new Map(prev);
      next.delete(itemId);
      return next;
    });
  };

  const updateAmount = (itemId: number, amount: number) => {
    setSelectedItems(prev => {
      const next = new Map(prev);
      const existing = next.get(itemId);
      if (existing) next.set(itemId, { ...existing, corrected_amount: amount });
      return next;
    });
  };

  const updateDueDateOverride = (itemId: number, dueDate: string) => {
    setSelectedItems(prev => {
      const next = new Map(prev);
      const existing = next.get(itemId);
      if (existing) next.set(itemId, { ...existing, due_date_override: dueDate });
      return next;
    });
  };

  const selectedList = Array.from(selectedItems.values());
  const totalAmount = selectedList.reduce((sum, item) => sum + (item.corrected_amount || 0), 0);
  const vendorName = vendors.find(v => v.id === selectedVendorId)?.name || '';

  const handleContinue = () => {
    if (selectedList.length === 0) {
      setError('Add at least one item to the invoice');
      return;
    }
    if (!invoiceFile) {
      setError('Please attach the invoice file');
      return;
    }
    if (!commonDueDate) {
      setError('Set the common payment due date');
      return;
    }
    setError('');
    setStep(2);
  };

  const handleSubmit = async () => {
    if (submitting || !selectedVendorId) return;
    setSubmitting(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('vendor_id', selectedVendorId.toString());
      formData.append('item_ids', JSON.stringify(selectedList.map(i => i.id)));
      const amounts: Record<number, number> = {};
      selectedList.forEach(i => { amounts[i.id] = i.corrected_amount; });
      formData.append('amounts', JSON.stringify(amounts));
      formData.append('due_date', commonDueDate);
      const overrides: Record<number, string> = {};
      selectedList.forEach(i => { if (i.due_date_override) overrides[i.id] = i.due_date_override; });
      formData.append('due_date_overrides', JSON.stringify(overrides));
      formData.append('notes', notes);
      if (invoiceFile) {
        formData.append('invoice_file', invoiceFile);
      }

      const response = await api.post('/booking/bulk-invoice', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        onSuccess();
        onClose();
      }
    } catch (err: any) {
      console.error('Error submitting bulk invoice:', err);
      setError(err?.response?.data?.detail || 'Error attaching invoice. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-lg w-full max-w-5xl h-full sm:h-[90vh] flex flex-col">
        <div className="flex justify-between items-center p-4 sm:p-6 border-b">
          <div>
            <h2 className="text-lg sm:text-xl font-bold">Bulk Invoice Upload</h2>
            <p className="text-sm text-gray-600">
              {step === 1
                ? 'Attach one invoice covering multiple tours from the same vendor'
                : `Review before attaching — ${selectedList.length} item(s), ${formatCurrency(totalAmount)}`}
            </p>
          </div>
          <button onClick={onClose} className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300">
            ✕ Close
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">
              {error}
            </div>
          )}

          {step === 1 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Left: vendor + search + results */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Vendor</label>
                <SearchableSelect
                  options={vendors.map(v => ({ value: v.id.toString(), label: v.name }))}
                  value={selectedVendorId ? selectedVendorId.toString() : ''}
                  onChange={(value) => setSelectedVendorId(value ? Number(value) : null)}
                  placeholder="Select vendor"
                  className="w-full mb-3"
                />

                {selectedVendorId && (
                  <>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Search by itinerary ID, quotation ID, guest name, or agency
                    </label>
                    <div className="relative mb-3">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g. 10190, WYN-Q-1023, Ahzab, Royal Trails"
                      />
                    </div>

                    <div className="border border-gray-200 rounded-lg divide-y max-h-[420px] overflow-y-auto">
                      {searching && (
                        <div className="p-4 text-sm text-gray-500 text-center">Searching...</div>
                      )}
                      {!searching && searchResults.length === 0 && (
                        <div className="p-4 text-sm text-gray-500 text-center">
                          No BOOKED items found for this vendor{searchText ? ' matching your search' : ''}.
                        </div>
                      )}
                      {searchResults.map((item) => {
                        const isSelected = selectedItems.has(item.id);
                        return (
                          <div key={item.id} className="p-3 flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <p className="font-medium text-gray-900 truncate">
                                WH_{item.itinerary_id} — {item.guest_name || 'Unknown guest'}
                              </p>
                              <p className="text-xs text-gray-600 truncate">
                                {item.name}
                                {item.agency_name ? ` · ${item.agency_name}` : ''}
                                {item.quotation_number ? ` · ${item.quotation_number}` : ''}
                              </p>
                              <p className="text-sm text-gray-700">{formatCurrency(item.estimated_amount)}</p>
                              {item.already_invoiced && (
                                <p className="text-xs text-amber-600 flex items-center gap-1 mt-0.5">
                                  <AlertTriangle className="h-3 w-3" /> Already has an invoice on file
                                </p>
                              )}
                            </div>
                            <button
                              onClick={() => addItem(item)}
                              disabled={item.already_invoiced || isSelected}
                              className={`px-3 py-1.5 text-sm rounded-lg whitespace-nowrap ${
                                item.already_invoiced || isSelected
                                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                  : 'bg-blue-600 text-white hover:bg-blue-700'
                              }`}
                            >
                              {isSelected ? 'Added' : 'Add'}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>

              {/* Right: selected list + amounts + file */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Items on this invoice ({selectedList.length})
                </label>
                <div className="border border-gray-200 rounded-lg divide-y max-h-[280px] overflow-y-auto mb-3">
                  {selectedList.length === 0 && (
                    <div className="p-4 text-sm text-gray-500 text-center">
                      Search and add items on the left to build this invoice.
                    </div>
                  )}
                  {selectedList.map((item) => (
                    <div key={item.id} className="p-3 flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate text-sm">
                          WH_{item.itinerary_id} — {item.name}
                        </p>
                        <p className="text-xs text-gray-600 truncate">{item.guest_name}</p>
                        <div className="mt-1 flex items-center gap-1">
                          <label className="text-xs text-gray-500">
                            Due {item.due_date_override ? '(override)' : `(common: ${commonDueDate || 'not set'})`}
                          </label>
                          <input
                            type="date"
                            value={item.due_date_override}
                            onChange={(e) => updateDueDateOverride(item.id, e.target.value)}
                            className="px-2 py-1 border border-gray-300 rounded text-xs"
                          />
                          {item.due_date_override && (
                            <button
                              type="button"
                              onClick={() => updateDueDateOverride(item.id, '')}
                              className="text-xs text-blue-600 hover:underline"
                            >
                              use common
                            </button>
                          )}
                        </div>
                      </div>
                      <input
                        type="number"
                        value={item.corrected_amount}
                        onChange={(e) => updateAmount(item.id, parseFloat(e.target.value) || 0)}
                        className="w-28 px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                      <button
                        onClick={() => removeItem(item.id)}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between text-sm font-medium text-gray-900 mb-4">
                  <span>Total</span>
                  <span>{formatCurrency(totalAmount)}</span>
                </div>

                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Due Date <span className="text-red-600">*</span>
                  <span className="ml-1 font-normal text-gray-500">— applies to all items unless overridden above</span>
                </label>
                <input
                  type="date"
                  value={commonDueDate}
                  onChange={(e) => setCommonDueDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mb-3"
                />

                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Invoice File <span className="text-red-600">*</span>
                </label>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  onChange={(e) => setInvoiceFile(e.target.files?.[0] || null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mb-3"
                />
                {invoiceFile && (
                  <div className="mb-3 flex items-center gap-2 text-sm text-green-600">
                    <FileText className="h-4 w-4" />
                    <span>{invoiceFile.name}</span>
                    <button onClick={() => setInvoiceFile(null)} className="text-red-600 hover:text-red-800">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                )}

                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Additional notes..."
                />
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                  <div><strong>Vendor:</strong> {vendorName}</div>
                  <div><strong>Total Amount:</strong> {formatCurrency(totalAmount)}</div>
                  <div><strong>Due Date:</strong> {commonDueDate}</div>
                  <div className="flex items-center gap-1">
                    <strong>Invoice:</strong>
                    <FileText className="h-4 w-4 text-green-700" />
                    <span>{invoiceFile?.name}</span>
                  </div>
                  {notes && <div className="sm:col-span-2"><strong>Notes:</strong> {notes}</div>}
                </div>
              </div>

              <h3 className="text-sm font-medium text-gray-700 mb-2">
                Items to be marked PENDING PAYMENT
              </h3>
              <div className="space-y-2">
                {selectedList.map((item) => (
                  <div key={item.id} className="bg-white border border-gray-200 rounded-lg p-3 flex justify-between items-center gap-3">
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 truncate">
                        WH_{item.itinerary_id} — {item.guest_name}
                      </p>
                      <p className="text-sm text-gray-600 truncate">{item.name}</p>
                      <p className="text-xs text-gray-500">
                        Due {item.due_date_override || commonDueDate}
                        {item.due_date_override && <span className="text-amber-600"> (override)</span>}
                      </p>
                    </div>
                    <p className="text-sm font-semibold text-gray-900 whitespace-nowrap">
                      {formatCurrency(item.corrected_amount)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 p-4 sm:p-6 border-t">
          {step === 1 && (
            <>
              <button onClick={onClose} className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={handleContinue}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Continue to Preview
              </button>
            </>
          )}
          {step === 2 && (
            <>
              <button
                onClick={() => setStep(1)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                disabled={submitting}
              >
                Back
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className={`flex items-center gap-2 px-4 py-2 text-white rounded-lg ${submitting ? 'bg-green-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`}
              >
                <CheckCircle className="h-4 w-4" />
                {submitting ? 'Attaching Invoice...' : 'Confirm & Attach Invoice'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkInvoiceModal;
